import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import React from "react";
import { cn } from "src/common/utils";

type TooltipPropsType = {
  content: React.ReactNode;
  children: React.ReactNode;
  delay?: number;
  position?: "top" | "bottom" | "left" | "right";
  maxWidth?: string;
  noWrap?: boolean;
  multiLine?: boolean;
  showArrow?: boolean;
  asChild?: boolean;
  /** When true, tooltip stays open when hovering over content (allows text selection/copying) */
  interactive?: boolean;
};

const sharedContentStyles = (position: string, noWrap: boolean, multiLine: boolean) =>
  cn(
    // Base styles
    "z-[9999]",
    "px-3 py-1.5 text-xs text-foreground",
    "bg-card/95",
    "rounded-md shadow-sm",
    "border border-border",
    // Animation
    "animate-in fade-in-0 zoom-in-95 duration-100",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
    // Position-based slide animations
    "data-[side=bottom]:slide-in-from-top-1",
    "data-[side=top]:slide-in-from-bottom-1",
    "data-[side=left]:slide-in-from-right-1",
    "data-[side=right]:slide-in-from-left-1",
    // Whitespace handling
    noWrap ? "whitespace-nowrap" : multiLine ? "whitespace-pre-line" : "whitespace-normal"
  );

export const Tooltip = React.memo<TooltipPropsType>(function Tooltip({
  content,
  children,
  delay = 200,
  position = "bottom",
  maxWidth = "20rem",
  noWrap = false,
  multiLine = false,
  showArrow = true,
  asChild = false,
  interactive = false,
}) {
  if (!content) {
    return <>{children}</>;
  }

  // Use HoverCard for interactive tooltips (allows hovering over content to select/copy)
  if (interactive) {
    return (
      <HoverCardPrimitive.Root openDelay={delay} closeDelay={100}>
        <HoverCardPrimitive.Trigger asChild={asChild} className={asChild ? undefined : "inline-flex"}>
          {children}
        </HoverCardPrimitive.Trigger>
        <HoverCardPrimitive.Portal>
          <HoverCardPrimitive.Content
            side={position}
            sideOffset={6}
            className={cn(sharedContentStyles(position, noWrap, multiLine), "select-text")}
            style={{ maxWidth }}
          >
            {content}
            {showArrow && (
              <HoverCardPrimitive.Arrow
                className="fill-card drop-shadow-sm"
                width={10}
                height={5}
              />
            )}
          </HoverCardPrimitive.Content>
        </HoverCardPrimitive.Portal>
      </HoverCardPrimitive.Root>
    );
  }

  // Default non-interactive tooltip
  // Note: Requires a TooltipProvider ancestor (added at app level)
  return (
    <TooltipPrimitive.Root delayDuration={delay}>
      <TooltipPrimitive.Trigger asChild={asChild} className={asChild ? undefined : "inline-flex"}>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={position}
          sideOffset={6}
          className={cn(sharedContentStyles(position, noWrap, multiLine), "select-none")}
          style={{ maxWidth }}
        >
          {content}
          {showArrow && (
            <TooltipPrimitive.Arrow
              className="fill-card drop-shadow-sm"
              width={10}
              height={5}
            />
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
});

// Also export Radix primitives for advanced use cases
export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
export const TooltipPortal = TooltipPrimitive.Portal;

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    showArrow?: boolean;
  }
>(({ className, sideOffset = 6, showArrow = true, children, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      // Base styles
      "z-[9999] select-none",
      "px-3 py-1.5 text-xs text-foreground",
      "bg-card/95",
      "rounded-md shadow-sm",
      "border border-border",
      // Animation
      "animate-in fade-in-0 zoom-in-95 duration-100",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
      // Position-based slide animations
      "data-[side=bottom]:slide-in-from-top-1",
      "data-[side=top]:slide-in-from-bottom-1",
      "data-[side=left]:slide-in-from-right-1",
      "data-[side=right]:slide-in-from-left-1",
      className
    )}
    {...props}
  >
    {children}
    {showArrow && (
      <TooltipPrimitive.Arrow
        className="fill-card drop-shadow-sm"
        width={10}
        height={5}
      />
    )}
  </TooltipPrimitive.Content>
));
TooltipContent.displayName = "TooltipContent";
